import type { AppSettings, CustomerReminder } from '@/lib/schemas/db'

/**
 * Customer reminder settings and wording. The scheduling itself lives on the
 * server (pocketbase/pb_hooks/lib/customer-reminders-core.js); these defaults
 * mirror its DEFAULTS so the Settings page can show what the server falls back
 * to when nothing has been configured.
 */
export interface ReminderSettingsInput {
  /** Delay before message 1, 2, 3… (1 counts from the start, the rest from the previous message). */
  scheduleSeconds: number[]
  /** Interval once the list runs out; 0 = stop after the list. */
  repeatSeconds: number
  maxAgeDays: number
}

export const REMINDER_DEFAULTS: ReminderSettingsInput = {
  scheduleSeconds: [0, 3 * 86_400],
  repeatSeconds: 7 * 86_400,
  maxAgeDays: 30,
}

/** The reminder settings in force: the admin's choices over the built-in defaults. */
export function resolveReminderSettings(
  settings?: AppSettings,
): ReminderSettingsInput {
  if (!settings?.reminderScheduleSeconds?.length) return REMINDER_DEFAULTS
  return {
    scheduleSeconds: settings.reminderScheduleSeconds,
    repeatSeconds: settings.reminderRepeatSeconds ?? REMINDER_DEFAULTS.repeatSeconds,
    maxAgeDays: settings.reminderMaxAgeDays ?? REMINDER_DEFAULTS.maxAgeDays,
  }
}

/** A human-readable problem with the settings, or null when they are valid. */
export function validateReminderSettings(
  input: ReminderSettingsInput,
): string | null {
  if (input.scheduleSeconds.length === 0) {
    return 'Add at least one reminder to the schedule.'
  }
  const whole = (n: number) => Number.isInteger(n) && n >= 0
  if (!input.scheduleSeconds.every(whole)) {
    return 'Each delay must be a whole, non-negative number of seconds.'
  }
  if (!whole(input.repeatSeconds)) {
    return 'The repeat interval must be 0 (no repeat) or more.'
  }
  if (!Number.isInteger(input.maxAgeDays) || input.maxAgeDays < 1) {
    return 'Stop reminding after at least 1 day.'
  }
  return null
}

export type DurationUnit = 'seconds' | 'minutes' | 'hours' | 'days'

const UNIT_SECONDS: Record<DurationUnit, number> = {
  seconds: 1,
  minutes: 60,
  hours: 3_600,
  days: 86_400,
}

export const DURATION_UNITS: DurationUnit[] = ['seconds', 'minutes', 'hours', 'days']

export function durationToSeconds(value: number, unit: DurationUnit): number {
  return Math.round(value * UNIT_SECONDS[unit])
}

/** The largest unit that divides the duration evenly (0 shows as 0 days). */
export function splitDuration(seconds: number): { value: number; unit: DurationUnit } {
  if (seconds === 0) return { value: 0, unit: 'days' }
  for (const unit of ['days', 'hours', 'minutes'] as const) {
    if (seconds % UNIT_SECONDS[unit] === 0) {
      return { value: seconds / UNIT_SECONDS[unit], unit }
    }
  }
  return { value: seconds, unit: 'seconds' }
}

/** "3 days", "30 seconds", "immediately". */
export function formatDuration(seconds: number): string {
  if (seconds === 0) return 'immediately'
  const { value, unit } = splitDuration(seconds)
  return `${value} ${value === 1 ? unit.slice(0, -1) : unit}`
}

/** What a reminder's status note means, for the Reminders page. */
const NOTE_LABELS: Record<string, string> = {
  '': '',
  stopped: 'Stopped by staff — no messages will be sent',
  expired: 'Expired — past the maximum age, no more messages',
  no_phone: 'No phone number on file',
  schedule_finished: 'Schedule finished — no further messages',
}

export function reminderNoteLabel(note: string): string {
  return NOTE_LABELS[note] ?? note
}

/** Whether staff can still do something useful with this reminder. */
export function canStopReminder(reminder: CustomerReminder): boolean {
  return reminder.state !== 'stopped' && reminder.state !== 'archived'
}

export function canResumeReminder(reminder: CustomerReminder): boolean {
  return reminder.state === 'stopped'
}
